import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Send, CheckCircle, Building2, Phone, Mail, MapPin } from 'lucide-react';
import axios from '../config/axios';

const Quote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    location: '',
    project_type: '',
    budget_range: '',
    timeline: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/quotes', formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        location: '',
        project_type: '',
        budget_range: '',
        timeline: '',
        message: ''
      });
      
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError('Failed to submit quotation request. Please try again.');
      console.error('Error submitting quote:', err);
    } finally {
      setLoading(false);
    }
  };

  const services = [
    'Building & Construction',
    'Civil & Road Works',
    'Water & Infrastructure',
    'Electrical Works',
    'Project Management',
    'Consultancy Services',
    'Supply of Materials',
    'Other'
  ];

  const projectTypes = [
    'New Construction',
    'Renovation/Remodeling',
    'Infrastructure Development',
    'Maintenance & Repairs',
    'Consultation Only'
  ];

  const budgetRanges = [
    'Under KES 1M',
    'KES 1M - 5M',
    'KES 5M - 10M',
    'KES 10M - 50M',
    'Above KES 50M',
    'To be discussed'
  ];

  const timelines = [
    'Urgent (Within 1 month)',
    '1-3 months',
    '3-6 months',
    '6-12 months',
    'Over 1 year',
    'Flexible'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Request a Quote - BURALE Maintainance & Suppliers | Get Professional Estimate</title>
        <meta name="description" content="Request a detailed quotation for your construction, infrastructure, or project management needs. Get a professional estimate from BURALE." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-br from-burale-dark to-burale-teal">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">REQUEST A QUOTE</h1>
          <p className="text-2xl text-burale-gold font-semibold mb-4">Get a Professional Estimate</p>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Tell us about your project and we'll provide you with a detailed quotation tailored to your needs.
          </p>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-20 bg-burale-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-burale-teal/10 rounded-xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-burale-teal rounded-full mb-6">
                <MapPin className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Visit Us</h3>
              <p className="text-gray-700 text-lg">Eastleigh Mall, Nairobi</p>
              <p className="text-gray-600 text-sm mt-2">P.O. Box 78246 - 00507</p>
            </div>

            <div className="bg-burale-teal/10 rounded-xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-burale-teal rounded-full mb-6">
                <Phone className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Call Us</h3>
              <p className="text-gray-700 text-lg">+254 721 542 569</p>
            </div>

            <div className="bg-burale-teal/10 rounded-xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-burale-teal rounded-full mb-6">
                <Mail className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Email Us</h3>
              <p className="text-gray-700 text-lg">info@buralesuppliers.co.ke</p>
            </div>
          </div>

          {/* Quote Form */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-burale-dark mb-4">Request Your Quotation</h2>
                <p className="text-gray-600">Fill out the form below with your project details and we'll get back to you with a comprehensive quote.</p>
              </div>

              {success && (
                <div className="mb-8 p-6 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-start">
                  <CheckCircle className="text-green-500 mr-4 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-green-800 font-semibold text-lg mb-1">Quotation Request Submitted!</h3>
                    <p className="text-green-700">Thank you for your interest. Our team will review your request and get back to you within 24-48 hours with a detailed quotation.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-8 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Company / Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                      placeholder="+254 XXX XXX XXX"
                    />
                  </div>
                </div>

                {/* Project Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Service Required *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Project Type *
                    </label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                    >
                      <option value="">Select project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-burale-dark mb-2">
                    Project Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                    placeholder="Project location"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Budget Range
                    </label>
                    <select
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                    >
                      <option value="">Select budget range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-burale-dark mb-2">
                      Expected Timeline
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-burale-dark mb-2">
                    Project Details / Requirements *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Tell us about your project requirements, specifications, and any other details that will help us provide an accurate quotation..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-burale-teal text-white py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Request Quotation
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-burale-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="mx-auto mb-6 text-burale-gold" size={64} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Immediate Assistance?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Our team is ready to discuss your project requirements and provide expert guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+254721542569" className="bg-burale-teal text-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 inline-flex items-center justify-center">
              <Phone className="mr-2" size={20} />
              Call Us Now
            </a>
            <a href="mailto:info@buralesuppliers.co.ke" className="bg-white text-burale-dark px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center">
              <Mail className="mr-2" size={20} />
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
